/**
 * Component Factory for Business-Type-Specific Booking Forms
 * 
 * This factory returns the appropriate form fields based on business type.
 * Each business type can have different required fields and components.
 */

import { ReactNode } from "react";

export type BusinessType = 
  | "GENERAL"
  | "HAIR_SALON"
  | "BARBER_SHOP"
  | "MEDICAL"
  | "FITNESS"
  | "RESTAURANT"
  | "BEAUTY_SPA"
  | "NAILS"
  | "PROFESSIONAL"
  | "EDUCATION"
  | "PROPERTY_RENTAL"
  | "DRIVING_INSTRUCTOR"
  | "SWIM_SCHOOL";

export type BookingFormField = {
  id: string;
  component: ReactNode;
  required?: boolean;
};

export type BookingFormConfig = {
  baseFields: BookingFormField[];
  typeSpecificFields: BookingFormField[];
  customFields?: BookingFormField[];
};

/**
 * Get booking form configuration based on business type and feature flags
 */
export function getBookingFormConfig(
  businessType: BusinessType,
  featureFlags: {
    requiresStaffSelection?: boolean;
    allowsGroupBookings?: boolean;
    requiresIntakeForm?: boolean;
  }
): BookingFormConfig {
  const baseFields: BookingFormField[] = [
    // Base fields are added by the main BookingForm component
    // (Service, Date, Time, Email, Name, Phone)
  ];

  const typeSpecificFields: BookingFormField[] = [];

  // Type-specific fields
  switch (businessType) {
    case "HAIR_SALON":
    case "BARBER_SHOP":
    case "BEAUTY_SPA":
    case "NAILS":
      if (featureFlags.requiresStaffSelection) {
        typeSpecificFields.push({
          id: "location",
          component: null, // Rendered by LocationSelector in stepped flow
          required: false, // Only required if multi-location
        });
        typeSpecificFields.push({
          id: "staff",
          component: null, // Rendered by StaffSelector in stepped flow
          required: true,
        });
      }
      break;

    case "MEDICAL":
      if (featureFlags.requiresStaffSelection) {
        typeSpecificFields.push({
          id: "staff",
          component: null,
          required: true,
        });
      }
      if (featureFlags.requiresIntakeForm) {
        typeSpecificFields.push({
          id: "intake",
          component: null, // Will be rendered by IntakeForm component
          required: true,
        });
      }
      break;

    case "RESTAURANT":
      if (featureFlags.allowsGroupBookings) {
        typeSpecificFields.push({
          id: "partySize",
          component: null, // Will be rendered by PartySizeField component
          required: true,
        });
      }
      typeSpecificFields.push({
        id: "dietaryRestrictions",
        component: null,
        required: false,
      });
      break;

    case "FITNESS":
    case "SWIM_SCHOOL":
      if (featureFlags.requiresStaffSelection) {
        typeSpecificFields.push({
          id: "staff",
          component: null,
          required: true,
        });
      }
      if (featureFlags.allowsGroupBookings) {
        typeSpecificFields.push({
          id: "classCapacity",
          component: null,
          required: false,
        });
      }
      break;

    case "PROFESSIONAL":
      if (featureFlags.requiresStaffSelection) {
        typeSpecificFields.push({
          id: "staff",
          component: null,
          required: true,
        });
      }
      typeSpecificFields.push({
        id: "consultationType",
        component: null,
        required: false,
      });
      break;

    case "EDUCATION":
      if (featureFlags.requiresStaffSelection) {
        typeSpecificFields.push({
          id: "staff",
          component: null,
          required: true,
        });
      }
      typeSpecificFields.push({
        id: "studentInfo",
        component: null,
        required: false,
      });
      break;

    case "DRIVING_INSTRUCTOR":
      if (featureFlags.requiresStaffSelection) {
        typeSpecificFields.push({
          id: "staff",
          component: null,
          required: true,
        });
      }
      typeSpecificFields.push({
        id: "pickupLocation",
        component: null,
        required: false,
      });
      typeSpecificFields.push({
        id: "dropoffLocation",
        component: null,
        required: false,
      });
      break;

    case "GENERAL":
    default:
      if (featureFlags.requiresStaffSelection) {
        typeSpecificFields.push({
          id: "staff",
          component: null,
          required: true,
        });
      }
      break;
  }

  return {
    baseFields,
    typeSpecificFields,
  };
}

/**
 * Get the order/sequence of fields for a business type.
 * For service-flow types with staff selection, the BookingForm uses a stepped UI
 * instead of this field order – but this is kept for backwards compatibility
 * and for business types that don't use the stepped flow.
 */
export function getFieldOrder(businessType: BusinessType): string[] {
  switch (businessType) {
    case "HAIR_SALON":
    case "BARBER_SHOP":
    case "BEAUTY_SPA":
    case "NAILS":
      // Stepped flow: service → location → professional → date/time → details
      return ["service", "location", "staff", "date", "time", "email", "name", "phone", "notes"];
    case "MEDICAL":
      return ["service", "staff", "date", "time", "intake", "email", "name", "phone", "notes"];
    case "RESTAURANT":
      return ["service", "date", "time", "partySize", "dietaryRestrictions", "email", "name", "phone", "notes"];
    case "FITNESS":
    case "SWIM_SCHOOL":
      return ["service", "staff", "date", "time", "classCapacity", "email", "name", "phone", "notes"];
    case "PROFESSIONAL":
      return ["service", "staff", "date", "time", "consultationType", "email", "name", "phone", "notes"];
    case "EDUCATION":
      return ["service", "staff", "date", "time", "studentInfo", "email", "name", "phone", "notes"];
    case "DRIVING_INSTRUCTOR":
      return ["service", "staff", "date", "time", "pickupLocation", "dropoffLocation", "email", "name", "phone", "notes"];
    case "PROPERTY_RENTAL":
      return ["service", "checkIn", "checkOut", "email", "name", "phone", "notes"];
    default:
      return ["service", "date", "time", "email", "name", "phone", "notes"];
  }
}
